import type { ParseEventOutput } from '@awwyeah/shared';
import type { EventDraft } from './components/EventFormSheet';
import { hhmmToMinutes } from './timeParsing';

/**
 * The "just say it" box on the new-event sheet: one sentence goes to the `parse-event`
 * relay task and comes back as a *proposal* (docs/AI-PIPELINE.md). Everything in this file
 * is the code half of that — pure functions, no React — turning the proposal into a patch
 * on the form's draft. Nothing is written to Yjs here: the filled-in form is the
 * confirmation step, and Save is still the only thing that writes.
 *
 * The model reads the sentence; code decides everything else. Who the event is for never
 * reaches the model at all — member names are matched here, against the household roster.
 */

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

function pad(value: number): string {
  return String(Math.abs(value)).padStart(2, '0');
}

/**
 * `now` as local wall clock with its UTC offset (`2026-10-11T20:15:00-06:00`) rather than
 * `toISOString()`: at 8pm in Denver the UTC date is already tomorrow, and "tomorrow" has to
 * resolve against the day the person is actually living in.
 */
export function localIsoWithOffset(date: Date): string {
  const offset = -date.getTimezoneOffset();
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  return `${day}T${time}${offset >= 0 ? '+' : '-'}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`;
}

/** The `context` sent alongside the sentence — what "tomorrow" and "Friday" resolve against. */
export function quickAddContext(now: Date): Record<string, unknown> {
  return {
    now: localIsoWithOffset(now),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekday: WEEKDAY_NAMES[now.getDay()],
  };
}

/**
 * Members named in the sentence, in roster order ("Kora's dentist", "pick up ezry"). Whole
 * words only, so a member called Al isn't on every event that mentions "also".
 */
export function ownersNamedIn(
  text: string,
  members: readonly { id: string; name: string }[],
): string[] {
  return members
    .filter((member) => {
      const name = member.name.trim();
      if (name.length === 0) return false;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`(^|[^\\p{L}\\p{N}])${escaped}($|[^\\p{L}\\p{N}])`, 'iu').test(text);
    })
    .map((member) => member.id);
}

/**
 * The proposal as a patch on the form's draft. Only what the sentence actually said is
 * touched: a null date leaves the day the sheet opened on, a missing time leaves the
 * draft's, and nobody named leaves the default owner.
 */
export function draftPatchFromProposal(
  proposal: ParseEventOutput,
  text: string,
  members: readonly { id: string; name: string }[],
): Partial<EventDraft> {
  const patch: Partial<EventDraft> = { title: proposal.title };
  if (proposal.date) patch.dateKey = proposal.date;

  if (proposal.startTime) {
    patch.allDay = false;
    patch.startTime = proposal.startTime;
    if (proposal.endTime) {
      const gap =
        (hhmmToMinutes(proposal.endTime) - hhmmToMinutes(proposal.startTime) + 1440) % 1440;
      if (gap >= 15) patch.durationMinutes = gap;
    }
  } else if (proposal.allDay && proposal.date) {
    patch.allDay = true;
  }

  if (proposal.location) patch.location = proposal.location;
  if (proposal.notes) patch.notes = proposal.notes;

  const ownerIds = ownersNamedIn(text, members);
  if (ownerIds.length > 0) patch.ownerIds = ownerIds;
  return patch;
}

/** The one line shown under the box once the form is filled — says what to double-check. */
export function quickAddNote(proposal: ParseEventOutput): string {
  if (!proposal.date) return "Filled in — didn't catch a day, so check the date.";
  if (proposal.confidence === 'low') return 'Filled in, but that was a guess — give it a look.';
  return 'Filled in below — check it and Save.';
}
